import {
	FloatingFocusManager,
	FloatingOverlay,
	FloatingPortal,
	useDismiss,
	useFloating,
	useId,
	useInteractions,
	useRole,
	useTransitionStyles,
} from '@floating-ui/react';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { BsXLg } from 'react-icons/bs';

import './BayFloatManager.scss';

/*
 * BayFloatManager — the overlay engine.
 *
 * Phase 1 of .plan/feat-overlay-consolidation. Read overview.md before changing
 * anything in here; the constraints below are not stylistic.
 *
 * WHY THIS EXISTS
 * ---------------
 * Three overlay libraries were each making a global decision on our behalf and
 * each getting it wrong, because none of them can know our layout:
 *
 *   - react-bootstrap's ModalManager SNAPSHOTS document.body's inline style and
 *     writes it back on close. Whoever snapshots second captures the other
 *     locker's lock as the "original" and restores it permanently -- drawer locks
 *     body, modal snapshots overflow:hidden as pristine, modal closes, the window
 *     can never scroll again.
 *   - It is also edge-triggered on 0<->1 with no hysteresis, so close-one-open-
 *     another tears down and sets back up inside a frame, re-padding .fixed-top /
 *     .sticky-top / .navbar-toggler each time. That is the top nav jumping.
 *   - Headless UI does the same snapshot on documentElement. rc-util does it on
 *     body. All three lock the wrong element: client scrolls #bayBody.
 *
 * Full account: docs/css-unification/overlay-and-zindex.md.
 *
 * THE RULES THIS COMPONENT KEEPS
 * ------------------------------
 * 1. It NEVER SNAPSHOTS AND RESTORES anyone's inline style. This is the rule that
 *    matters, and it is not the same as "never lock". Locking is fine; freezing
 *    someone else's lock is what breaks. On release we reset to `''`, so the worst
 *    case is clearing a value the app set, never permanently re-applying another
 *    locker's `overflow: hidden`.
 *
 *    `FloatingOverlay`'s own `lockScroll` follows the same discipline -- globally
 *    ref-counted via a module-level counter, and its cleanup assigns `overflow: ''`
 *    rather than a remembered value. That is exactly why it is safe to use and
 *    react-bootstrap's manager is not.
 * 2. It locks WHAT IT IS TOLD, and nothing more. `lockScroll` handles the window;
 *    `scrollLockTarget` handles an app whose real scroller is some wrapper. bay-ui
 *    must never hardcode a consumer's container -- `#bayBody` does not appear in
 *    this file and must not.
 * 3. Defaults follow the mechanism, not convenience. A centred dialog locks,
 *    because the page scrolling behind it is wrong.
 *
 *    The edge placements default to NOT locking, on the grounds that a drawer is
 *    `position: fixed` against an edge and physically covers the scroll container's
 *    scrollbar. CORRECTION (2026-08-10): that is only true when the app scrolls an
 *    element INSIDE the content area -- client's `#bayBody`. It is FALSE when the app
 *    scrolls the WINDOW, because a fixed element's containing block is the initial
 *    containing block, which excludes the window scrollbar. The drawer stops at its
 *    inner edge and the page scrollbar stays visible beside the drawer's own.
 *
 *    Observed in smc, whose legacy stylesheet pins html and body to `height: 100%`
 *    so overflowing content propagates to the viewport. Reported as "double scroll
 *    bars"; centred dialogs never showed it because they already lock.
 *
 *    The default here is left alone because the engine cannot know the consumer's
 *    scroller -- that is rule 2. `BayDrawer` defaults `lockScroll` to `true` and
 *    exposes it, which is the right level: it is a component an app configures.
 * 4. z-index comes from the named scale with a literal fallback, never a bare
 *    integer.
 * 5. Every hardcoded className is MERGED with the caller's via clsx, never
 *    followed by a spread that could carry the same key. A spread wins, which is
 *    how BayModal silently lost `bay-modal` and BayProductSwitcher lost
 *    `bay-product-switcher`.
 * 6. No component is defined inside another component's body -- that produces a
 *    new type every render and remounts the DOM node, dropping :hover and focus.
 *
 * WHY FLOATING UI RATHER THAN A NEW DEPENDENCY
 * -------------------------------------------
 * @floating-ui/react was already installed and already bundled (BayColorPicker).
 * It gives us the parts that must never be hand-rolled -- focus trap, focus
 * restore, background aria-hidden -- and its scroll lock is opt-in. It is also
 * where Headless UI v2 delegates positioning, so this is the destination either
 * way.
 *
 * `useFloating` here is used for its CONTEXT ONLY, not for positioning. A modal is
 * not anchored to a reference element; placement is CSS. That is the documented
 * Floating UI dialog pattern.
 */

const TRANSITION_MS = 200;

/** Named z-index steps. See styles/theme_default/_zindex.scss. */
// How the panel enters, per placement. This is what makes one engine read as two
// different components without being two components.
//
// A centred dialog DROPS AND SCALES, and fades with the backdrop -- that is modal
// language.
//
// A drawer SLIDES from the edge it is pinned to, at full opacity. It deliberately
// does not fade and does not scale: a drawer that fades in reads as a modal that
// happens to be against the wall, which is the "drawers act like modals" report.
// A slide is the whole visual affordance -- it says "this came from off-screen and
// will go back", and scaling or fading undercuts it.
//
// Drawers declare NO `opacity` at all -- not even `opacity: 1`. Floating UI derives
// `transition-property` from the keys of these objects
// (`Object.keys(openStyles).map(camelCaseToKebabCase).join(',')`), so naming opacity
// here puts it in the transitioned property list even though the value never
// changes. rc-drawer transitions `transform` and nothing else on the element that
// moves; matching that keeps the property list to exactly what animates.
const PANEL_ENTER = {
	center: { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.98)' },
	start: { transform: 'translateX(-100%)' },
	end: { transform: 'translateX(100%)' },
	top: { transform: 'translateY(-100%)' },
	bottom: { transform: 'translateY(100%)' },
};

// Where each placement comes to rest. Same transform FUNCTION as its entry state so
// the browser interpolates within the function rather than decomposing to a matrix:
// `translateX(100%) -> translateX(0)`, not `translate3d(...) -> none`. rc-drawer uses
// `translateX`/`translateY` for the same reason.
const PANEL_REST = {
	center: { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
	start: { transform: 'translateX(0)' },
	end: { transform: 'translateX(0)' },
	top: { transform: 'translateY(0)' },
	bottom: { transform: 'translateY(0)' },
};

// Drawer motion, matched EXACTLY to rc-drawer -- `$duration: 0.3s` and
// `$ease-in-out-circ` in BayDrawer.scss, which are in turn rc-drawer's own
// `assets/index.css` values. Every drawer in the estate has moved on this curve
// for about five years, so a new engine that moves differently reads as broken
// even when it is smooth.
//
// The first version used `cubic-bezier(0.32, 0.72, 0, 1)` at 280ms -- the iOS
// sheet curve -- and it read as "bounces in". Nothing overshoots (no control
// point exceeds y=1), but that curve is a hard ease-OUT: its first control point
// sits at y=0.72, so the panel covers ~72% of the travel in the first third and
// then decelerates through the remaining two thirds. A fast arrival followed by a
// long settle is perceived as an overshoot even though the panel never passes its
// mark. `ease-in-out-circ` is symmetric -- slow off the edge, quick through the
// middle, slow to rest -- so there is no arrival spike to settle from.
//
// Exposed as a custom property (with the literal as fallback) so a consumer can
// retune it, same as `--bay-float-dim-bg` and `--bay-float-top`. There is no
// shared easing token in the theme yet; when one lands, this reads it for free.
const DRAWER_MS = 300;
const DRAWER_EASE =
	'var(--bay-float-drawer-ease, cubic-bezier(0.78, 0.14, 0.15, 0.86))';

const SIZES = ['sm', 'md', 'lg', 'xl', 'fullscreen'];

const Z = {
	backdrop: 'var(--bs-zindex-modal-backdrop, 1050)',
	panel: 'var(--bs-zindex-modal, 1055)',
};

interface BayFloatManagerHeaderProps {
	children?: React.ReactNode;
	className?: string;
	onClose?: () => void;
	closeButton?: boolean;
}

interface BayFloatManagerBodyProps {
	children?: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
}

interface BayFloatManagerFooterProps {
	children?: React.ReactNode;
	className?: string;
}

const BayFloatManagerHeader = ({ children, className, onClose, closeButton = true }: BayFloatManagerHeaderProps) => (
	<div className={clsx('bay-float-header', className)}>
		<div className="bay-float-title-slot">{children}</div>
		{closeButton && (
			<button
				aria-label="Close"
				className="bay-float-close"
				type="button"
				onClick={onClose}
			>
				<BsXLg size={14} />
			</button>
		)}
	</div>
);

const BayFloatManagerBody = ({ children, className, style }: BayFloatManagerBodyProps) => (
	<div className={clsx('bay-float-body', className)} style={style}>
		{children}
	</div>
);

const BayFloatManagerFooter = ({ children, className }: BayFloatManagerFooterProps) => (
	<div className={clsx('bay-float-footer', className)}>{children}</div>
);

export interface BayFloatManagerProps {
	/** Whether the overlay is open. */
	show?: boolean;
	/** Called when the user dismisses -- close button, ESC, or outside press. */
	onClose?: () => void;
	/** Called once the exit transition has finished and the tree has unmounted. */
	onExited?: () => void;
	/** DOM id for the panel. */
	id?: string;
	/** Header content. When omitted no header renders and `aria-label` is required. */
	title?: React.ReactNode;
	children?: React.ReactNode;
	/** Footer content. When omitted no footer renders. */
	footer?: React.ReactNode;
	size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
	/** Explicit width (px number or CSS length). Overrides `size`. */
	width?: number | string;
	/** `center` is a modal; the others are drawers. */
	placement?: 'center' | 'start' | 'end' | 'top' | 'bottom';
	closeButton?: boolean;
	/** `true` dims, `false` is invisible but still captures, `'static'` blocks outside dismissal. */
	backdrop?: boolean | 'static';
	/** Freeze the window while open. Defaults true for center, false for drawers. */
	lockScroll?: boolean;
	/** Element or ref for the app's real scroll container, when not the window. */
	scrollLockTarget?: any;
	dismissOnEscape?: boolean;
	dismissOnOutsideClick?: boolean;
	className?: string;
	backdropClassName?: string;
	bodyClassName?: string;
	/** Inline style for the body (dynamic values a class cannot express). */
	bodyStyle?: React.CSSProperties;
	/** Additional className merged onto the wrapper div around `children`, inside the body. */
	contentClassName?: string;
	headerClassName?: string;
	footerClassName?: string;
	/** Ref or index for the element to focus on open. Defaults to the panel. */
	initialFocus?: React.RefObject<any> | number;
	'aria-label'?: string;
	/**
	 * Overrides the backdrop's z-index. Needed when a drawer opens OVER another
	 * drawer or modal: without it the second backdrop sits at the same layer as
	 * the first panel and disappears behind it.
	 *
	 * Typed `number | string` deliberately -- callers pass a TOKEN, not an integer
	 * (`"var(--bs-zindex-modal, 1055)"`), per CLAUDE.md's rule against inventing
	 * z-index values. master's propTypes declared this `number` while BayNavDrawer
	 * passed a string; that mismatch is corrected here.
	 */
	zIndexBackdrop?: number | string;
}

const BayFloatManager = ({
	show = false,
	onClose,
	onExited,
	id,
	title,
	children,
	footer,
	size = 'md',
	width,
	placement = 'center',
	closeButton = true,
	backdrop = true,
	lockScroll,
	scrollLockTarget,
	dismissOnEscape = true,
	dismissOnOutsideClick = true,
	className,
	backdropClassName,
	bodyClassName,
	bodyStyle,
	contentClassName = '',
	headerClassName,
	footerClassName,
	initialFocus,
	'aria-label': ariaLabel,
	zIndexBackdrop,
}: BayFloatManagerProps) => {
	const labelId = useId();
	const describedById = useId();

	// Appearance props are LATCHED while open, and only released once the exit
	// transition has finished.
	//
	// Consumers naturally derive these from the same state that drives `show`:
	//
	//   <BayFloatManager placement={which || 'center'} show={!!which} />
	//
	// The moment `which` clears, `placement` snaps back to its default while the
	// panel is still animating out -- so a right-hand drawer jumps to the middle of
	// the screen and fades from there. Latching means the panel leaves the way it
	// arrived regardless of what the caller does to these props on the way out.
	//
	// Only cosmetic/structural props are latched. `show` obviously is not, and
	// neither are callbacks, which must stay current.
	const latched = useRef({ placement, size, title, footer });
	if (show) {
		latched.current = { placement, size, title, footer };
	}
	const view = show ? { placement, size, title, footer } : latched.current;

	// Fall back rather than trusting the value through to a class name. An
	// unrecognised `placement` produced `bay-float-backdrop-undefined`, a class
	// with no rules at all -- so the panel dropped to the default flex position in
	// the top-left corner and looked like it had teleported. propTypes warns about
	// it, but a warning in the console does not stop the layout collapsing, and the
	// value can arrive as null from a caller clearing state mid-open.
	const activePlacement = PANEL_ENTER[view.placement] ? view.placement : 'center';
	const activeSize = SIZES.includes(view.size) ? view.size : 'md';
	const activeTitle = view.title;
	const activeFooter = view.footer;

	// Identifies OUR scrim to the outside-press predicate below.
	const backdropRef = useRef<HTMLDivElement>(null);

	// `useFloating` for CONTEXT only -- see the note at the top. No middleware,
	// no reference element; placement is CSS.
	const { refs, context } = useFloating({
		open: show,
		onOpenChange: (nextOpen) => {
			if (!nextOpen) onClose?.();
		},
	});

	const dismiss = useDismiss(context, {
		// `backdrop === 'static'` means the overlay stays put on an outside press --
		// same contract as react-bootstrap, so call sites read the same.

		// A PREDICATE, not a boolean: a float opened FROM this one is a SIBLING, not a
		// child (BayNavDrawer's modals), and there is no FloatingTree here -- so a
		// press on that dialog's own buttons read as an outside press and dismissed
		// this float, from a document listener, which is why z-index never fixed it.
		outsidePress: (event) => {
			if (!dismissOnOutsideClick || backdrop === 'static') return false;

			const target = event.target as Element | null;
			if (!target) return true;

			// Our own scrim is the click-outside surface (our panel never reaches here).
			if (backdropRef.current?.contains(target)) return true;

			// Anything inside ANOTHER float is not an outside press.
			return !target.closest('.bay-float, .bay-float-backdrop');
		},
		escapeKey: dismissOnEscape,
		// mousedown rather than click: a click that STARTS inside the panel and ends
		// outside it (text selection dragged past the edge) must not dismiss.
		outsidePressEvent: 'mousedown',
	});
	const role = useRole(context, { role: 'dialog' });
	const { getFloatingProps } = useInteractions([dismiss, role]);

	// TWO transition sets, not one. The first version applied a single set -- which
	// included `transform: translateY(-8px) scale(0.98)` -- to the BACKDROP, so the
	// whole dimmed layer slid and scaled while the panel itself never faded. That
	// is the "fade is part of the modal and comes in weirdly" bug.
	//
	// The backdrop only ever fades. The panel fades and moves, and a drawer slides
	// in from its own edge rather than dropping from above.
	const isDrawer = activePlacement !== 'center';

	const { isMounted, styles: backdropStyles } = useTransitionStyles(context, {
		// Matched to the panel's duration so the dim and the panel arrive together.
		duration: isDrawer ? DRAWER_MS : TRANSITION_MS,
		initial: { opacity: 0 },
		open: { opacity: 1 },
		// The dim needs the panel's CURVE too, not just its duration. Without this it
		// falls back to the browser default `ease` while the panel runs the circ
		// curve, so over 300ms the two visibly drift apart -- the scrim leads going
		// in and lags coming out. rc-drawer matches them for the same reason: its
		// mask is `opacity 0.3s $ease-in-out-circ`, identical to its panel.
		common: isDrawer ? { transitionTimingFunction: DRAWER_EASE } : undefined,
	});

	const { styles: panelStyles } = useTransitionStyles(context, {
		duration: isDrawer ? DRAWER_MS : TRANSITION_MS,
		initial: PANEL_ENTER[activePlacement],
		open: PANEL_REST[activePlacement],
		common: isDrawer ? { transitionTimingFunction: DRAWER_EASE } : undefined,
	});

	// Default follows the mechanism: a centred dialog locks, a drawer does not.
	// See rule 3 at the top before changing this.
	const shouldLock = lockScroll ?? activePlacement === 'center';

	// Lock a consumer-named container, for apps whose real scroller is not the
	// window. client scrolls `#bayBody`, so `lockScroll` alone does nothing
	// there -- but bay-ui must not know that ID, so the app passes the element.
	//
	// Note the cleanup: `overflow = ''`, NOT a remembered value. Restoring a
	// snapshot is what lets one locker freeze another's lock permanently, which is
	// the react-bootstrap failure this engine exists to avoid. Resetting to empty
	// can at worst clear an inline value the app set itself, which is recoverable
	// and visible; freezing a lock is neither.
	useEffect(() => {
		if (!isMounted || !shouldLock || !scrollLockTarget) return undefined;
		const el = scrollLockTarget.current ?? scrollLockTarget;
		if (!el?.style) return undefined;
		el.style.overflow = 'hidden';
		return () => {
			el.style.overflow = '';
		};
	}, [isMounted, shouldLock, scrollLockTarget]);

	// Fire onExited once, when the exit transition has finished and Floating UI has
	// unmounted the tree. `useTransitionStyles` owns the timing, so we watch its
	// result rather than running a timer of our own -- react-confirm's hardcoded
	// 1000ms unmountDelay is the mistake this avoids.
	const wasMounted = useRef(false);
	useEffect(() => {
		if (isMounted) {
			wasMounted.current = true;
			return;
		}
		if (wasMounted.current) {
			wasMounted.current = false;
			onExited?.();
		}
	}, [isMounted, onExited]);

	if (!isMounted) return null;

	return (
		<FloatingPortal>
			{/*
			 * `lockScroll` locks the WINDOW (Floating UI ref-counts it globally and
			 * releases to `''`, never to a snapshot). For an app whose real scroller
			 * is a wrapper, pass `scrollLockTarget` as well -- see rule 2.
			 */}
			{/*
			 * The dim is a SEPARATE absolutely-positioned layer, and the fade is applied
			 * to that -- not to FloatingOverlay itself.
			 *
			 * `opacity` on a parent applies to its subtree. Fading the overlay faded the
			 * panel inside it too, so a drawer's slide was masked by the whole layer
			 * fading in and it read as "drawers fade like modals". The panel's own
			 * opacity could not fix that: it was already 1, and 1 inside a parent at 0.4
			 * is still 0.4 on screen.
			 *
			 * With the dim as a sibling, the overlay stays fully opaque, the dim fades,
			 * and the panel does exactly what its own transition says -- slide only for a
			 * drawer, fade and scale for a centred dialog.
			 */}
			<FloatingOverlay
				ref={backdropRef}
				lockScroll={shouldLock}
				className={clsx(
					'bay-float-backdrop',
					`bay-float-backdrop-${activePlacement}`,
					backdrop === false && 'bay-float-backdrop-transparent',
					backdropClassName,
				)}
				style={{
					zIndex: zIndexBackdrop ?? Z.backdrop,
					// `clip`, NOT `hidden`. Both override FloatingOverlay's own inline
					// `overflow: auto` -- inline is required to win, because Floating UI
					// spreads our style after its defaults, so a stylesheet rule would lose
					// to its inline value -- and both clip identically. The difference is
					// the one that matters here:
					//
					//   `hidden` still makes the box a SCROLL CONTAINER. It only blocks
					//   scrolling by the USER; script, and the browser's own focus and
					//   scrollIntoView machinery, can still scroll it.
					//   `clip` is not a scroll container at all.
					//
					// Why that matters: FloatingFocusManager focuses `focusableElements[0]`
					// on open and passes `preventScroll: elToFocus === floatingFocusElement`
					// -- so preventScroll is FALSE whenever it focuses a control INSIDE the
					// panel rather than the panel itself. On a drawer that control starts at
					// `translateX(100%)`, off-screen, so the browser scrolls this box across
					// to reveal it. Then as the panel slides home the rightward overflow
					// shrinks to nothing and `scrollLeft` clamps back to 0. The whole layer
					// jumps sideways and settles at the end of the slide -- reported as "it
					// over slides then corrects".
					//
					// It is NOT the easing curve. That curve cannot overshoot: no control
					// point exceeds y=1, and x(t) is monotonic. Two commits went at the
					// curve before the cause turned out to be a scroll.
					//
					// A transformed descendant DOES contribute to an ancestor's scrollable
					// overflow, which is why the transform alone was enough to create
					// something scrollable.
					//
					// `clip` closes the same hole for content, not just entry: react-select
					// calls scrollIntoView when it opens a menu, and under `hidden` that
					// would shift the layer every time a select inside a drawer was opened.
					//
					// The original reason for clipping still holds: under Floating UI's
					// `overflow: auto`, a drawer entering from outside the viewport makes a
					// real scrollbar appear for the length of the slide and then vanish.
					// Nothing is lost by clipping -- the panel is capped at
					// `max-height: 100%` and `.bay-float-body` scrolls internally, so this
					// box never needs to scroll on the panel's behalf.
					//
					// Support: Chrome 90+, Firefox 81+, Safari 16+ -- all inside bay-ui's
					// browserslist (`last 2 versions, not dead`). On anything older the
					// declaration is invalid and dropped, leaving Floating UI's `auto`: a
					// cosmetic scrollbar during the slide, not a functional break.
					overflow: 'clip',
					// Lets an app inset the whole overlay below its own fixed chrome, without
					// bay-ui knowing anything about that chrome. Set the custom property on a
					// class passed via `backdropClassName`:
					//
					//   .my-drawer-backdrop { --bay-float-top: 50px; }
					//
					// This has to be an inline `top` for the same reason `overflow` does:
					// FloatingOverlay writes `top: 0` in its own inline style. A stylesheet
					// rule cannot beat an inline declaration -- and note it writes the four
					// LONGHANDS (`top/right/bottom/left`), not the `inset` shorthand, so this
					// one longhand overrides it without disturbing the other three.
					// `...rest.style` is spread last, so ours wins. Verified in
					// @floating-ui/react 0.27.20.
					//
					// The dim is `position: absolute; inset: 0` INSIDE this element, so it
					// picks up the inset automatically and never covers the chrome either.
					top: 'var(--bay-float-top, 0)',
				}}
			>
				<div className="bay-float-dim" style={backdropStyles} />
				<FloatingFocusManager
					context={context}
					initialFocus={initialFocus}
					// Restores focus to whatever opened the overlay, and to the parent
					// overlay rather than the page when a nested one closes.
					returnFocus
					modal
				>
					<div
						ref={refs.setFloating}
						// On the PANEL, not the backdrop. Consumers and E2E locators address
						// the drawer itself (`#nav_drawer`), and under rc-drawer that id
						// landed on the element carrying the drawer classes.
						id={id}
						aria-describedby={children ? describedById : undefined}
						// Floating UI does NOT set this. `useRole({ role: 'dialog' })` sets
						// `role` and nothing else -- verified in its source, and caught by
						// BayFloatManager.test.jsx rather than by reading the docs.
						//
						// It is correct to assert here: `FloatingFocusManager` is rendered with
						// `modal`, which marks every sibling `aria-hidden`, so the rest of the
						// page really is inert. `aria-modal` is the declarative signal for that;
						// without it a screen reader is told the background is hidden but not
						// that this is a modal context.
						aria-modal="true"
						aria-label={activeTitle ? undefined : ariaLabel}
						aria-labelledby={activeTitle ? labelId : undefined}
						className={clsx(
							'bay-float',
							`bay-float-${activeSize}`,
							`bay-float-${activePlacement}`,
							className,
						)}
						style={{
							zIndex: Z.panel,
							// Dynamic, so it sets the custom property and the stylesheet owns the
							// real `width` -- the --bay-pdf-viewer-height pattern. Inline, so it
							// beats the size class's own `--bay-float-width` with no specificity
							// fight.
							...(width == null
								? {}
								: {
									'--bay-float-width':
											typeof width === 'number' ? `${width}px` : width,
								}),
							...panelStyles,
						}}
						{...getFloatingProps()}
					>
						{activeTitle && (
							<BayFloatManagerHeader
								className={headerClassName}
								closeButton={closeButton}
								onClose={onClose}
							>
								<h2 className="bay-float-title" id={labelId}>
									{activeTitle}
								</h2>
							</BayFloatManagerHeader>
						)}
						<BayFloatManagerBody className={bodyClassName} style={bodyStyle}>
							<div id={describedById} className={clsx(contentClassName)}>{children}</div>
						</BayFloatManagerBody>
						{activeFooter && (
							<BayFloatManagerFooter className={footerClassName}>
								{activeFooter}
							</BayFloatManagerFooter>
						)}
					</div>
				</FloatingFocusManager>
			</FloatingOverlay>
		</FloatingPortal>
	);
};

BayFloatManager.Header = BayFloatManagerHeader;
BayFloatManager.Body = BayFloatManagerBody;
BayFloatManager.Footer = BayFloatManagerFooter;

export default BayFloatManager;
